import { useState, useEffect, useCallback } from 'react'
import { X, Trash2, Plus, Printer, FileText, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PageSpinner } from '@/components/ui/Spinner'
import {
  useInvoices,
  useBuyers,
  useStoreSupplyItems,
  useSaveInvoice,
  useBuyerProducts,
} from '@/hooks/useBuyers'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabase'
import { RezzAiPastePanel } from '@/components/ai/RezzAiPastePanel'
import { cn } from '@/lib/utils'
import type { Invoice, InvoiceStatus, Buyer, MenuItem, PaymentTerms, Restaurant } from '@/types/app'

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  InvoiceStatus,
  { label: string; badgeClass: string; btnClass: string }
> = {
  draft: { label: 'Draft', badgeClass: 'bg-stone-100 text-stone-500', btnClass: 'border-stone-300 text-stone-600 hover:bg-stone-50' },
  sent: { label: 'Sent', badgeClass: 'bg-blue-100 text-blue-700', btnClass: 'border-blue-300 text-blue-700 hover:bg-blue-50' },
  partial: { label: 'Partial', badgeClass: 'bg-amber-100 text-amber-700', btnClass: 'border-amber-300 text-amber-700 hover:bg-amber-50' },
  paid: { label: 'Paid', badgeClass: 'bg-green-100 text-green-700', btnClass: 'border-green-300 text-green-700 hover:bg-green-50' },
  overdue: { label: 'Overdue', badgeClass: 'bg-red-100 text-red-700', btnClass: 'border-red-300 text-red-700 hover:bg-red-50' },
  cancelled: { label: 'Cancelled', badgeClass: 'bg-stone-200 text-stone-500', btnClass: 'border-stone-300 text-stone-400 hover:bg-stone-50' },
}

const STATUSES: InvoiceStatus[] = ['draft', 'sent', 'partial', 'paid', 'overdue', 'cancelled']

const PAYMENT_TERMS: PaymentTerms[] = ['COD', 'Due on Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net EOM']

const PAYMENT_TERMS_LABELS: Record<PaymentTerms, string> = {
  'COD': 'COD — Cash on Delivery',
  'Due on Receipt': 'Due on Receipt',
  'Net 15': 'Net 15',
  'Net 30': 'Net 30',
  'Net 45': 'Net 45',
  'Net 60': 'Net 60',
  'Net EOM': 'Net EOM — End of Month',
}

function calcDueDate(issuedDate: string, terms: PaymentTerms): string {
  if (!issuedDate) return ''
  const d = new Date(issuedDate + 'T00:00:00')
  switch (terms) {
    case 'COD':
    case 'Due on Receipt':
      return issuedDate
    case 'Net 15': d.setDate(d.getDate() + 15); break
    case 'Net 30': d.setDate(d.getDate() + 30); break
    case 'Net 45': d.setDate(d.getDate() + 45); break
    case 'Net 60': d.setDate(d.getDate() + 60); break
    case 'Net EOM':
      // Last calendar day of the invoice's issued month
      return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0]
  }
  return d.toISOString().split('T')[0]
}

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const cfg = STATUS_CONFIG[status]
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.badgeClass}`}>
      {cfg.label}
    </span>
  )
}

// ─── Line item row state ──────────────────────────────────────────────────────

interface LineRow {
  key: string
  menu_item_id: string
  description: string
  upc_code: string
  qty: string
  unit_price: string
}

function newRow(): LineRow {
  return {
    key: crypto.randomUUID(),
    menu_item_id: '',
    description: '',
    upc_code: '',
    qty: '1',
    unit_price: '',
  }
}

// ─── Print helper ─────────────────────────────────────────────────────────────

function fmtPrintDate(dateStr: string): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function printInvoice(
  invoice: Invoice,
  restaurant: Restaurant | null,
  storeItems: MenuItem[]
): void {
  const regionalNameMap = new Map<string, string>()
  for (const item of storeItems) {
    if (item.regional_name) {
      regionalNameMap.set(item.id, item.regional_name)
    }
  }

  const lineItems = invoice.line_items ?? []
  const grandTotal = lineItems.reduce((sum, li) => sum + li.qty * li.unit_price, 0)

  const primaryContact = invoice.buyer?.contact_name ?? null
  const primaryPhone = invoice.buyer?.phone ?? null

  const restaurantName = restaurant?.name ?? ''
  const restaurantAddress = restaurant?.address ?? ''

  const lineRowsHtml = lineItems
    .map((li, idx) => {
      const regionalName =
        li.regional_name ??
        (li.menu_item_id ? regionalNameMap.get(li.menu_item_id) ?? '' : '')
      const lineTotal = li.qty * li.unit_price
      return `
        <tr>
          <td style="padding:6px 8px;border:1px solid #333;text-align:center">${idx + 1}</td>
          <td style="padding:6px 8px;border:1px solid #333;font-family:monospace;font-size:11px">${li.upc_code ?? '—'}</td>
          <td style="padding:6px 8px;border:1px solid #333">${li.description}</td>
          <td style="padding:6px 8px;border:1px solid #333">${regionalName}</td>
          <td style="padding:6px 8px;border:1px solid #333;text-align:right">$${li.unit_price.toFixed(2)}</td>
          <td style="padding:6px 8px;border:1px solid #333;text-align:center">${li.qty}</td>
          <td style="padding:6px 8px;border:1px solid #333;text-align:right">$${lineTotal.toFixed(2)}</td>
        </tr>`
    })
    .join('')

  const emptyRows = [1, 2, 3, 4]
    .map(
      () => `
        <tr>
          <td style="padding:10px 8px;border:1px solid #333">&nbsp;</td>
          <td style="padding:10px 8px;border:1px solid #333">&nbsp;</td>
          <td style="padding:10px 8px;border:1px solid #333">&nbsp;</td>
          <td style="padding:10px 8px;border:1px solid #333">&nbsp;</td>
          <td style="padding:10px 8px;border:1px solid #333">&nbsp;</td>
          <td style="padding:10px 8px;border:1px solid #333">&nbsp;</td>
          <td style="padding:10px 8px;border:1px solid #333">&nbsp;</td>
        </tr>`
    )
    .join('')

  const logoSrc = restaurant?.logo_url ?? ''
  const logoHtml = logoSrc
    ? `<img src="${logoSrc}" style="height:60px;width:auto;display:block;" onerror="this.style.display='none'">`
    : `<div style="font-size:22px;font-weight:700;color:#333;">${restaurantName}</div>`

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice ${invoice.invoice_number ?? 'Draft'}</title>
</head>
<body style="font-family:Arial,sans-serif;color:#000;margin:0;padding:20px;font-size:13px;">

  <!-- Header -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;">
    <div style="min-width:80px;">
      ${logoHtml}
    </div>
    <div style="text-align:right;">
      <div style="font-size:18px;font-weight:bold;">${restaurantName}</div>
      ${restaurantAddress ? `<div style="margin-top:4px;white-space:pre-wrap;">${restaurantAddress}</div>` : ''}
    </div>
  </div>

  <!-- Bill To + Invoice Meta -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;">
    <div>
      <div style="font-size:11px;font-weight:bold;text-transform:uppercase;color:#555;margin-bottom:4px;">Bill To</div>
      <div style="font-weight:bold;font-size:14px;">${invoice.buyer?.name ?? '—'}</div>
      ${invoice.buyer?.address ? `<div style="margin-top:3px;white-space:pre-wrap;">${invoice.buyer.address}</div>` : ''}
      ${primaryContact || primaryPhone
      ? `<div style="margin-top:4px;">${[primaryContact, primaryPhone].filter(Boolean).join(' · ')}</div>`
      : ''}
    </div>
    <div style="text-align:right;">
      <table style="border-collapse:collapse;margin-left:auto;">
        <tr>
          <td style="padding:2px 8px 2px 0;font-weight:bold;">Invoice #:</td>
          <td style="padding:2px 0;font-family:monospace;">${invoice.invoice_number ?? 'Draft'}</td>
        </tr>
        <tr>
          <td style="padding:2px 8px 2px 0;font-weight:bold;">Date:</td>
          <td style="padding:2px 0;">${fmtPrintDate(invoice.issued_date)}</td>
        </tr>
        ${invoice.due_date
      ? `<tr>
              <td style="padding:2px 8px 2px 0;font-weight:bold;">Due Date:</td>
              <td style="padding:2px 0;">${fmtPrintDate(invoice.due_date)}</td>
            </tr>`
      : ''}
        <tr>
          <td style="padding:2px 8px 2px 0;font-weight:bold;">Payment Terms:</td>
          <td style="padding:2px 0;">${invoice.payment_terms}</td>
        </tr>
        ${invoice.po_number
      ? `<tr>
              <td style="padding:2px 8px 2px 0;font-weight:bold;">PO #:</td>
              <td style="padding:2px 0;">${invoice.po_number}</td>
            </tr>`
      : ''}
      </table>
    </div>
  </div>

  <!-- Table title -->
  <div style="text-align:center;font-size:16px;font-weight:bold;margin-bottom:8px;">Delivered Items</div>

  <!-- Line items table -->
  <table style="width:100%;border-collapse:collapse;margin-bottom:8px;">
    <thead>
      <tr style="background:#f0f0f0;">
        <th style="padding:7px 8px;border:1px solid #333;text-align:center;font-size:12px;">#No.</th>
        <th style="padding:7px 8px;border:1px solid #333;text-align:left;font-size:12px;">UPC Code</th>
        <th style="padding:7px 8px;border:1px solid #333;text-align:left;font-size:12px;">Primary Name</th>
        <th style="padding:7px 8px;border:1px solid #333;text-align:left;font-size:12px;">Telugu Name</th>
        <th style="padding:7px 8px;border:1px solid #333;text-align:right;font-size:12px;">Price</th>
        <th style="padding:7px 8px;border:1px solid #333;text-align:center;font-size:12px;">Delivery Qty</th>
        <th style="padding:7px 8px;border:1px solid #333;text-align:right;font-size:12px;">TOTAL</th>
      </tr>
    </thead>
    <tbody>
      ${lineRowsHtml || `<tr><td colspan="7" style="padding:12px;border:1px solid #333;text-align:center;color:#888;">No items</td></tr>`}
      ${emptyRows}
    </tbody>
  </table>

  <!-- Grand total -->
  <div style="text-align:right;font-size:16px;font-weight:bold;margin-top:8px;margin-bottom:24px;">
    Total&nbsp;&nbsp;$${grandTotal.toFixed(2)}
  </div>

  <!-- Footer: terms + signature -->
  <div style="display:flex;gap:40px;align-items:flex-start;margin-top:16px;">
    <div style="border:1px solid #333;padding:12px 16px;min-width:260px;font-size:12px;line-height:1.7;">
      <div style="font-weight:bold;margin-bottom:6px;">Terms &amp; Instructions</div>
      <div>- All claims must be made in writing within 3 days of merchandise received.</div>
      <div>- No returns without prior approval.</div>
      <div>- Payment due per invoice terms.</div>
      ${restaurantName || restaurantAddress
      ? `<div style="margin-top:6px;">- Mail check to:<br>&nbsp;&nbsp;${restaurantName}${restaurantAddress ? '<br>&nbsp;&nbsp;' + restaurantAddress.replace(/\n/g, '<br>&nbsp;&nbsp;') : ''}</div>`
      : ''}
    </div>
    <div style="flex:1;font-size:13px;padding-top:8px;">
      <div style="margin-bottom:6px;">Delivery Received By:</div>
      <div style="border-bottom:1px solid #333;min-width:260px;height:80px;margin-bottom:20px;"></div>
      
    </div>
  </div>

</body>
</html>`

  const win = window.open('', '_blank')
  if (!win) return
  win.document.write(html)
  win.document.close()
  setTimeout(() => win.print(), 200)
}

// ─── Invoice form state ───────────────────────────────────────────────────────

interface InvoiceFormState {
  id?: string
  invoiceNumber?: string
  buyer_id: string
  issued_date: string
  due_date: string
  po_number: string
  payment_terms: PaymentTerms
  tax_percent: string
  discount_amount: string
  amount_paid: string
  payment_notes: string
  notes: string
  status: InvoiceStatus
}

function emptyInvoiceForm(): InvoiceFormState {
  return {
    buyer_id: '',
    issued_date: new Date().toISOString().split('T')[0],
    due_date: '',
    po_number: '',
    payment_terms: 'Net 30',
    tax_percent: '0',
    discount_amount: '0',
    amount_paid: '0',
    payment_notes: '',
    notes: '',
    status: 'draft',
  }
}

// ─── Invoice modal ────────────────────────────────────────────────────────────

function InvoiceModal({
  invoice,
  buyers,
  supplyItems,
  onClose,
  readOnly,
}: {
  invoice: Invoice | null
  buyers: Buyer[]
  supplyItems: MenuItem[]
  onClose: () => void
  readOnly: boolean
}) {
  const saveInvoice = useSaveInvoice()
  const restaurant = useAuthStore(s => s.restaurant)

  const [form, setForm] = useState<InvoiceFormState>(() =>
    invoice
      ? {
        id: invoice.id,
        invoiceNumber: invoice.invoice_number,
        buyer_id: invoice.buyer_id,
        issued_date: invoice.issued_date,
        due_date: invoice.due_date ?? '',
        po_number: invoice.po_number ?? '',
        payment_terms: invoice.payment_terms,
        tax_percent: invoice.tax_percent.toString(),
        discount_amount: invoice.discount_amount.toString(),
        amount_paid: invoice.amount_paid.toString(),
        payment_notes: invoice.payment_notes ?? '',
        notes: invoice.notes ?? '',
        status: invoice.status,
      }
      : emptyInvoiceForm()
  )

  const [rows, setRows] = useState<LineRow[]>(() => {
    if (invoice?.line_items && invoice.line_items.length > 0) {
      return invoice.line_items.map(li => ({
        key: li.id,
        menu_item_id: li.menu_item_id ?? '',
        description: li.description,
        upc_code: li.upc_code ?? '',
        qty: li.qty.toString(),
        unit_price: li.unit_price.toString(),
      }))
    }
    return [newRow()]
  })

  const [error, setError] = useState<string | null>(null)

  // RezzAI: build or import an invoice from pasted text
  const [aiBusy, setAiBusy] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [aiSummary, setAiSummary] = useState<string | null>(null)

  const activeBuyers = buyers.filter(b => b.is_active)
  const selectedBuyer = buyers.find(b => b.id === form.buyer_id)

  // Contract products: once a buyer has any marked, the product picker (manual
  // and RezzAI) defaults to just those instead of the full catalog.
  const { data: contractRows = [] } = useBuyerProducts(form.buyer_id)
  const [showAllProducts, setShowAllProducts] = useState(false)
  const contractItemIds = new Set(contractRows.map(cp => cp.menu_item_id))
  const hasContractProducts = contractRows.length > 0
  // What manual row-pickers show — respects the "show all" toggle.
  const pickerProducts = hasContractProducts && !showAllProducts
    ? supplyItems.filter(p => contractItemIds.has(p.id))
    : supplyItems
  // What RezzAI is allowed to match against — always prefers the contract
  // list when one exists, for both precision and "only real products" intent.
  const aiProducts = hasContractProducts
    ? supplyItems.filter(p => contractItemIds.has(p.id))
    : supplyItems

  // Reset the "show all" toggle when the buyer changes — adjusted during
  // render (not an effect) per React's guidance for this exact case.
  const [productsResetKey, setProductsResetKey] = useState(form.buyer_id)
  if (productsResetKey !== form.buyer_id) {
    setProductsResetKey(form.buyer_id)
    setShowAllProducts(false)
  }

  function productOptionsFor(menuItemId: string) {
    if (!menuItemId || pickerProducts.some(p => p.id === menuItemId)) return pickerProducts
    // Keep an already-picked product visible even if it falls outside the
    // current filter (e.g. set before a contract list existed).
    const current = supplyItems.find(p => p.id === menuItemId)
    return current ? [current, ...pickerProducts] : pickerProducts
  }

  // Fills the invoice from free text — either a short instruction ("invoice
  // Suvidha for 6 big idly…") or a whole existing invoice pasted in to import.
  // Replaces the blank starter row but never silently drops rows you've typed.
  async function handleAiParse(text: string) {
    setAiBusy(true)
    setAiError(null)
    setAiSummary(null)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('parse-invoice', {
        body: {
          raw_text: text,
          today: new Date().toISOString().split('T')[0],
          buyers: activeBuyers.map(b => ({ id: b.id, name: b.name, code: b.buyer_code })),
          products: aiProducts.map(p => ({
            id: p.id, name: p.name,
            price: p.price, wholesale_price: p.wholesale_price,
          })),
        },
      })
      if (fnError) throw new Error(fnError.message)

      const parsed = data as {
        buyer_id: string | null
        buyer_name_raw: string | null
        issued_date: string | null
        due_date: string | null
        po_number: string | null
        payment_terms: PaymentTerms | null
        tax_percent: number | null
        discount_amount: number | null
        amount_paid: number | null
        notes: string | null
        line_items: { menu_item_id: string; description: string; qty: number; unit_price: number | null }[]
        unmatched_items: string[]
      }

      setForm(f => ({
        ...f,
        buyer_id: f.buyer_id || parsed.buyer_id || '',
        issued_date: parsed.issued_date || f.issued_date,
        due_date: f.due_date || parsed.due_date || '',
        po_number: f.po_number || parsed.po_number || '',
        payment_terms: parsed.payment_terms && PAYMENT_TERMS.includes(parsed.payment_terms) ? parsed.payment_terms : f.payment_terms,
        tax_percent: parsed.tax_percent != null ? String(parsed.tax_percent) : f.tax_percent,
        discount_amount: parsed.discount_amount != null ? String(parsed.discount_amount) : f.discount_amount,
        amount_paid: parsed.amount_paid != null ? String(parsed.amount_paid) : f.amount_paid,
        notes: [f.notes, parsed.notes].filter(Boolean).join(' · '),
      }))

      // The edge function only ever returns line items tied to a real catalog
      // id (enforced server-side too) — nothing here invents a new product.
      const rawNewRows = (parsed.line_items ?? []).map((li): LineRow | null => {
        const product = supplyItems.find(p => p.id === li.menu_item_id)
        if (!product) return null
        const price = li.unit_price ?? product.wholesale_price ?? product.price ?? 0
        return {
          key: crypto.randomUUID(),
          menu_item_id: product.id,
          description: product.name,
          upc_code: product.upc_code ?? '',
          qty: String(li.qty ?? 1),
          unit_price: String(price ?? ''),
        }
      })
      const newRows = rawNewRows.filter((r): r is LineRow => r !== null)

      // Drop the untouched placeholder row, keep anything already filled in.
      setRows(prev => {
        const kept = prev.filter(r => r.description.trim() || r.menu_item_id)
        return [...kept, ...newRows]
      })

      const unmatchedList = parsed.unmatched_items ?? []
      const buyerNote = !parsed.buyer_id && parsed.buyer_name_raw
        ? ` · buyer "${parsed.buyer_name_raw}" not on file — pick one`
        : ''
      setAiSummary(
        `Filled in from your text · ${newRows.length} item${newRows.length === 1 ? '' : 's'} added`
        + (unmatchedList.length ? ` · couldn't match: ${unmatchedList.join(', ')} — add manually` : '')
        + buyerNote
      )
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Could not read that text. Check that ANTHROPIC_API_KEY is set in Supabase Edge Function secrets.')
    } finally {
      setAiBusy(false)
    }
  }

  useEffect(() => {
    if (!form.buyer_id) return
    const buyer = buyers.find(b => b.id === form.buyer_id)
    if (buyer?.default_payment_terms) {
      setForm(f => ({ ...f, payment_terms: buyer.default_payment_terms as PaymentTerms }))
    }
  }, [form.buyer_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = rows.reduce((sum, r) => {
    return sum + (parseFloat(r.qty) || 0) * (parseFloat(r.unit_price) || 0)
  }, 0)
  const taxPct = parseFloat(form.tax_percent) || 0
  const taxAmt = subtotal * (taxPct / 100)
  const discount = parseFloat(form.discount_amount) || 0
  const grandTotal = subtotal + taxAmt - discount
  const amountPaid = parseFloat(form.amount_paid) || 0
  const balanceDue = grandTotal - amountPaid

  const suggestedStatus: InvoiceStatus | null =
    amountPaid > 0 && amountPaid < grandTotal - 0.005
      ? 'partial'
      : amountPaid >= grandTotal - 0.005 && grandTotal > 0
        ? 'paid'
        : null

  function handleProductChange(key: string, productId: string) {
    const product = supplyItems.find(p => p.id === productId)
    setRows(prev =>
      prev.map(r =>
        r.key === key
          ? {
            ...r,
            menu_item_id: productId,
            description: product?.name ?? r.description,
            upc_code: product?.upc_code ?? '',
            unit_price:
              product?.wholesale_price?.toString() ??
              product?.price?.toString() ??
              r.unit_price,
          }
          : r
      )
    )
  }

  function handleRowChange(key: string, field: keyof Omit<LineRow, 'key'>, value: string) {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, [field]: value } : r)))
  }

  function addRow() {
    setRows(prev => [...prev, newRow()])
  }

  function removeRow(key: string) {
    setRows(prev => (prev.length > 1 ? prev.filter(r => r.key !== key) : prev))
  }

  const handlePrint = useCallback(() => {
    if (!invoice) return
    printInvoice(invoice, restaurant, supplyItems)
  }, [invoice, restaurant, supplyItems])

  async function handleSave() {
    if (!form.buyer_id) {
      setError('Please select a buyer')
      return
    }
    setError(null)
    try {
      const lineItems = rows
        .filter(r => r.description.trim())
        .map(r => {
          const menuItem = r.menu_item_id
            ? supplyItems.find(p => p.id === r.menu_item_id)
            : undefined
          return {
            menu_item_id: r.menu_item_id || null,
            description: r.description.trim(),
            upc_code: r.upc_code.trim() || null,
            regional_name: menuItem?.regional_name ?? null,
            qty: parseFloat(r.qty) || 1,
            unit_price: parseFloat(r.unit_price) || 0,
          }
        })

      await saveInvoice.mutateAsync({
        invoice: {
          id: form.id,
          buyer_id: form.buyer_id,
          status: form.status,
          issued_date: form.issued_date,
          due_date: form.due_date || null,
          po_number: form.po_number.trim() || null,
          payment_terms: form.payment_terms,
          tax_percent: parseFloat(form.tax_percent) || 0,
          discount_amount: parseFloat(form.discount_amount) || 0,
          amount_paid: parseFloat(form.amount_paid) || 0,
          payment_notes: form.payment_notes.trim() || null,
          notes: form.notes.trim() || null,
        },
        lineItems,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save invoice')
    }
  }

  const isNew = !form.id

  const inputCls =
    'flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-stone-50 disabled:text-stone-400'

  const labelCls = 'text-sm font-medium text-stone-700'

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-stone-900">
              {isNew ? 'New Invoice' : form.invoiceNumber ? `Invoice #${form.invoiceNumber}` : 'Edit Invoice'}
            </h2>
            <StatusBadge status={form.status} />
          </div>
          <div className="flex items-center gap-2">
            {invoice && (
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer size={14} />
                Print
              </Button>
            )}
            {!readOnly && (
              <Button size="sm" onClick={handleSave} loading={saveInvoice.isPending}>
                {isNew ? 'Create Invoice' : 'Save Changes'}
              </Button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-stone-400 hover:text-stone-600 ml-1"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto">
          {error && <p className="text-sm text-danger mb-4">{error}</p>}

          <div className="flex gap-6">
            {/* Left — main content */}
            <div className="flex-1 min-w-0 space-y-6">

              {/* RezzAI — build or import an invoice from pasted text */}
              {!readOnly && (
                <RezzAiPastePanel
                  cta="Ask RezzAI — type a request or paste an invoice…"
                  title="Describe the invoice, or paste an existing one to import"
                  placeholder={'e.g. Create invoice for Suvidha Suwanee for 6 big idly, 4 small idli, 10 big dosa, 5 millet dosa small\n\n…or paste a full invoice from Word/email and RezzAI will import its date, items and prices.'}
                  rows={5}
                  busy={aiBusy}
                  error={aiError}
                  summary={aiSummary}
                  onRun={handleAiParse}
                  onDismissError={() => setAiError(null)}
                />
              )}

              {/* Buyer */}
              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>
                  Buyer <span className="text-danger">*</span>
                </label>
                <select
                  value={form.buyer_id}
                  onChange={e => setForm(f => ({ ...f, buyer_id: e.target.value }))}
                  disabled={readOnly}
                  className={inputCls}
                >
                  <option value="">Select buyer...</option>
                  {activeBuyers.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.buyer_code ? `[${b.buyer_code}] ` : ''}{b.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Line items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-stone-700">Line Items</p>
                    {hasContractProducts && !readOnly && (
                      <div className="flex items-center gap-1 bg-stone-100 p-0.5 rounded-lg" title={`${selectedBuyer?.name ?? 'This buyer'}'s product list`}>
                        <button
                          type="button"
                          onClick={() => setShowAllProducts(false)}
                          className={cn(
                            'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                            !showAllProducts ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                          )}
                        >
                          Contracted ({contractRows.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAllProducts(true)}
                          className={cn(
                            'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                            showAllProducts ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                          )}
                        >
                          All ({supplyItems.length})
                        </button>
                      </div>
                    )}
                  </div>
                  {!readOnly && (
                    <Button variant="outline" size="sm" onClick={addRow}>
                      <Plus size={13} />
                      Add Item
                    </Button>
                  )}
                </div>

                <div className="rounded border border-stone-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-stone-50 border-b border-stone-200">
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide w-36">
                          Product
                        </th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                          Description
                        </th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide w-28">
                          UPC
                        </th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide w-16">
                          Qty
                        </th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide w-28">
                          Unit Price
                        </th>
                        <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide w-24">
                          Total
                        </th>
                        {!readOnly && <th className="w-8" />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => {
                        const qty = parseFloat(row.qty) || 0
                        const price = parseFloat(row.unit_price) || 0
                        const lineTotal = qty * price

                        return (
                          <tr key={row.key} className="border-b border-stone-100 last:border-0">
                            <td className="px-3 py-2">
                              <select
                                value={row.menu_item_id}
                                onChange={e => handleProductChange(row.key, e.target.value)}
                                disabled={readOnly}
                                className="h-8 w-full rounded border border-stone-300 bg-white px-2 text-xs text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-stone-50 disabled:text-stone-400"
                              >
                                <option value="">— pick product —</option>
                                {productOptionsFor(row.menu_item_id).map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.description}
                                onChange={e => handleRowChange(row.key, 'description', e.target.value)}
                                disabled={readOnly}
                                placeholder="Description"
                                className="h-8 w-full rounded border border-stone-300 bg-white px-2 text-xs text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-stone-50"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={row.upc_code}
                                onChange={e => handleRowChange(row.key, 'upc_code', e.target.value)}
                                disabled={readOnly}
                                placeholder="UPC"
                                className="h-8 w-full rounded border border-stone-300 bg-white px-2 text-xs font-mono text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-stone-50"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={row.qty}
                                onChange={e => handleRowChange(row.key, 'qty', e.target.value)}
                                disabled={readOnly}
                                className="h-8 w-full rounded border border-stone-300 bg-white px-2 text-xs text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-stone-50"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-xs pointer-events-none">
                                  $
                                </span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={row.unit_price}
                                  onChange={e => handleRowChange(row.key, 'unit_price', e.target.value)}
                                  disabled={readOnly}
                                  placeholder="0.00"
                                  className="h-8 w-full pl-5 pr-2 rounded border border-stone-300 bg-white text-xs text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-stone-50"
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right text-xs font-medium text-stone-800">
                              ${lineTotal.toFixed(2)}
                            </td>
                            {!readOnly && (
                              <td className="px-2 py-2">
                                <button
                                  type="button"
                                  onClick={() => removeRow(row.key)}
                                  disabled={rows.length === 1}
                                  className="text-stone-300 hover:text-danger transition-colors disabled:opacity-30"
                                  title="Remove row"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Notes</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  disabled={readOnly}
                  placeholder="Optional notes for the buyer"
                  className="flex w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none disabled:bg-stone-50 disabled:text-stone-400"
                />
              </div>
            </div>

            {/* Right sidebar */}
            <div className="w-64 shrink-0 space-y-4">

              {/* Dates & meta */}
              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Issued Date</label>
                <input
                  type="date"
                  value={form.issued_date}
                  onChange={e => {
                    const issued = e.target.value
                    const due = calcDueDate(issued, form.payment_terms)
                    setForm(f => ({ ...f, issued_date: issued, due_date: due || f.due_date }))
                  }}
                  disabled={readOnly}
                  className={inputCls}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Due Date</label>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                  disabled={readOnly}
                  className={inputCls}
                />
                {form.payment_terms === 'Net EOM' && form.due_date && (
                  <p className="text-2xs text-amber-600 font-medium">
                    End of {new Date(form.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} — consolidated with all EOM invoices
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Payment Terms</label>
                <select
                  value={form.payment_terms}
                  onChange={e => {
                    const terms = e.target.value as PaymentTerms
                    const due = calcDueDate(form.issued_date, terms)
                    setForm(f => ({ ...f, payment_terms: terms, due_date: due || f.due_date }))
                  }}
                  disabled={readOnly}
                  className={inputCls}
                >
                  {PAYMENT_TERMS.map(t => (
                    <option key={t} value={t}>{PAYMENT_TERMS_LABELS[t]}</option>
                  ))}
                </select>
                {selectedBuyer?.default_payment_terms &&
                  selectedBuyer.default_payment_terms !== form.payment_terms && (
                    <button
                      type="button"
                      onClick={() =>
                        setForm(f => ({
                          ...f,
                          payment_terms: selectedBuyer.default_payment_terms as PaymentTerms,
                        }))
                      }
                      className="self-start text-xs text-brand-600 hover:text-brand-700"
                    >
                      Reset to buyer default ({selectedBuyer.default_payment_terms})
                    </button>
                  )}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>PO Number</label>
                <input
                  type="text"
                  value={form.po_number}
                  onChange={e => setForm(f => ({ ...f, po_number: e.target.value }))}
                  disabled={readOnly}
                  placeholder="Buyer's reference"
                  className={inputCls}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Tax %</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.tax_percent}
                  onChange={e => setForm(f => ({ ...f, tax_percent: e.target.value }))}
                  disabled={readOnly}
                  placeholder="0"
                  className={inputCls}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Discount $</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm pointer-events-none">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.discount_amount}
                    onChange={e => setForm(f => ({ ...f, discount_amount: e.target.value }))}
                    disabled={readOnly}
                    placeholder="0.00"
                    className={`${inputCls} pl-7`}
                  />
                </div>
              </div>

              {/* Totals block */}
              <div className="rounded border border-stone-200 bg-stone-50 p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-stone-500">Subtotal</span>
                  <span className="font-medium text-stone-800">${subtotal.toFixed(2)}</span>
                </div>
                {taxPct > 0 && (
                  <div className="flex justify-between">
                    <span className="text-stone-500">Tax ({taxPct}%)</span>
                    <span className="font-medium text-stone-800">${taxAmt.toFixed(2)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-stone-500">Discount</span>
                    <span className="font-medium text-stone-800">-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-stone-300 pt-1.5">
                  <span className="font-semibold text-stone-900">Total</span>
                  <span className="font-bold text-stone-900">${grandTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Amount Paid</span>
                  <span className="font-medium text-stone-800">${amountPaid.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={balanceDue > 0.005 ? 'font-semibold text-red-600' : 'text-stone-500'}>
                    Balance Due
                  </span>
                  <span className={`font-bold ${balanceDue > 0.005 ? 'text-red-600' : 'text-stone-800'}`}>
                    ${balanceDue.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Payment */}
              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Amount Paid</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm pointer-events-none">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.amount_paid}
                    onChange={e => setForm(f => ({ ...f, amount_paid: e.target.value }))}
                    disabled={readOnly}
                    placeholder="0.00"
                    className={`${inputCls} pl-7`}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Payment Notes</label>
                <textarea
                  rows={2}
                  value={form.payment_notes}
                  onChange={e => setForm(f => ({ ...f, payment_notes: e.target.value }))}
                  disabled={readOnly}
                  placeholder='e.g. "Paid by check #123"'
                  className="flex w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none disabled:bg-stone-50 disabled:text-stone-400"
                />
              </div>

              {/* Status selector */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label className={labelCls}>Status</label>
                  {suggestedStatus && suggestedStatus !== form.status && (
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, status: suggestedStatus! }))}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                    >
                      Suggest: {STATUS_CONFIG[suggestedStatus].label}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {STATUSES.map(s => {
                    const cfg = STATUS_CONFIG[s]
                    const isSelected = form.status === s
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => !readOnly && setForm(f => ({ ...f, status: s }))}
                        disabled={readOnly}
                        className={[
                          'px-2 py-1.5 rounded border text-xs font-semibold transition-colors',
                          isSelected
                            ? `${cfg.badgeClass} border-current`
                            : cfg.btnClass,
                          readOnly ? 'cursor-default' : 'cursor-pointer',
                        ].join(' ')}
                      >
                        {cfg.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        {!readOnly && (
          <div className="px-6 py-4 border-t border-stone-200 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saveInvoice.isPending}>
              {isNew ? 'Create Invoice' : 'Save Changes'}
            </Button>
          </div>
        )}
        {readOnly && (
          <div className="px-6 py-4 border-t border-stone-200 flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Invoices page ────────────────────────────────────────────────────────────

export function InvoicesPage() {
  const { data: invoices, isLoading: loadingInvoices } = useInvoices()
  const { data: buyers, isLoading: loadingBuyers } = useBuyers()
  const { data: supplyItems, isLoading: loadingSupply } = useStoreSupplyItems()
  const restaurant = useAuthStore(s => s.restaurant)
  const role = useAuthStore(s => s.user?.role)

  const [modalInvoice, setModalInvoice] = useState<Invoice | null | 'new'>(null)
  const [invoiceView, setInvoiceView] = useState<'list' | 'by-buyer'>('list')
  const [expandedBuyer, setExpandedBuyer] = useState<string | null>(null)

  const isLoading = loadingInvoices || loadingBuyers || loadingSupply

  if (isLoading) return <PageSpinner />

  const all = invoices ?? []
  const allBuyers = buyers ?? []
  const allSupplyItems = supplyItems ?? []

  const canEdit = role === 'owner' || role === 'ops'

  function getBuyer(buyerId: string): Buyer | undefined {
    return allBuyers.find(b => b.id === buyerId)
  }

  function computeTotal(inv: Invoice): number {
    const items = inv.line_items ?? []
    const sub = items.reduce((s, li) => s + li.qty * li.unit_price, 0)
    const tax = sub * ((inv.tax_percent ?? 0) / 100)
    const disc = inv.discount_amount ?? 0
    return sub + tax - disc
  }

  function computeSubtotal(inv: Invoice): number {
    return (inv.line_items ?? []).reduce((s, li) => s + li.qty * li.unit_price, 0)
  }

  const now = new Date()
  const thisMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

  const monthsData = (() => {
    const result = []
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label = d.toLocaleDateString('en-US', { month: 'short' })
      const monthInvs = all.filter(inv => (inv.issued_date ?? '').startsWith(key))
      const invoiced = monthInvs.reduce((s, inv) => s + computeTotal(inv), 0)
      const collected = monthInvs.reduce((s, inv) => s + (inv.amount_paid ?? 0), 0)
      result.push({ key, label, invoiced, collected, isCurrent: i === 0 })
    }
    return result
  })()

  const thisMonthInvs = all.filter(inv => (inv.issued_date ?? '').startsWith(thisMonthKey))
  const thisMonthTotal = thisMonthInvs.reduce((s, inv) => s + computeTotal(inv), 0)
  const thisMonthPaid = thisMonthInvs.reduce((s, inv) => s + (inv.amount_paid ?? 0), 0)
  const lastMonthTotal = monthsData[monthsData.length - 2]?.invoiced ?? 0
  const growthPct = lastMonthTotal > 0 ? ((thisMonthTotal - lastMonthTotal) / lastMonthTotal * 100) : null

  const outstandingTotal = all
    .filter(inv => !['paid', 'cancelled'].includes(inv.status))
    .reduce((s, inv) => s + Math.max(0, computeTotal(inv) - (inv.amount_paid ?? 0)), 0)

  const overdueInvs = all.filter(inv => {
    if (['paid', 'cancelled'].includes(inv.status)) return false
    if (!inv.due_date) return false
    return new Date(inv.due_date + 'T23:59:59') < now
  })
  const overdueTotal = overdueInvs.reduce((s, inv) => s + Math.max(0, computeTotal(inv) - (inv.amount_paid ?? 0)), 0)

  // Per-buyer rollup (only buyers who have at least one invoice)
  const buyerStats = allBuyers
    .map(buyer => {
      const invs = all.filter(inv => inv.buyer_id === buyer.id)
      const active = invs.filter(inv => inv.status !== 'cancelled')
      const totalInv = active.reduce((s, inv) => s + computeTotal(inv), 0)
      const totalPaid = active.reduce((s, inv) => s + (inv.amount_paid ?? 0), 0)
      const outstanding = active
        .filter(inv => inv.status !== 'paid')
        .reduce((s, inv) => s + Math.max(0, computeTotal(inv) - (inv.amount_paid ?? 0)), 0)
      const hasOverdue = active.some(inv =>
        inv.status !== 'paid' && inv.due_date && new Date(inv.due_date + 'T23:59:59') < now
      )
      return { buyer, invs, active, totalInv, totalPaid, outstanding, hasOverdue }
    })
    .filter(b => b.invs.length > 0)
    .sort((a, b) => b.totalInv - a.totalInv)

  return (
    <div className="p-6 space-y-6">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Invoices</h1>
          <p className="text-sm text-stone-500 mt-0.5">
            Wholesale invoices sent to buyers
          </p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setModalInvoice('new')}>
            + New Invoice
          </Button>
        )}
      </div>

      {all.length > 0 && (
        <div className="flex gap-4 flex-wrap">
          <span className="text-sm text-stone-500">
            <span className="font-semibold text-stone-800">{all.length}</span> total
          </span>
          {(['draft', 'sent', 'partial', 'paid', 'overdue'] as InvoiceStatus[]).map(s => {
            const count = all.filter(i => i.status === s).length
            if (count === 0) return null
            return (
              <span key={s} className="text-sm text-stone-400">
                ·&nbsp;
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${STATUS_CONFIG[s].badgeClass}`}>
                  {count} {STATUS_CONFIG[s].label}
                </span>
              </span>
            )
          })}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card p-4">
          <p className="section-label">Invoiced This Month</p>
          <p className="text-2xl font-bold text-stone-900 mt-1">${thisMonthTotal.toFixed(0)}</p>
          {growthPct !== null && (
            <p className={cn('text-xs mt-1 font-medium', growthPct >= 0 ? 'text-success-dark' : 'text-danger')}>
              {growthPct >= 0 ? '↑' : '↓'} {Math.abs(growthPct).toFixed(0)}% vs last month
            </p>
          )}
        </div>
        <div className="card p-4">
          <p className="section-label">Collected This Month</p>
          <p className="text-2xl font-bold text-success-dark mt-1">${thisMonthPaid.toFixed(0)}</p>
          {thisMonthTotal > 0 && (
            <p className="text-xs mt-1 text-stone-400">{Math.round(thisMonthPaid / thisMonthTotal * 100)}% of invoiced</p>
          )}
        </div>
        <div className="card p-4">
          <p className="section-label">Outstanding</p>
          <p className={cn('text-2xl font-bold mt-1', outstandingTotal > 0 ? 'text-warning-dark' : 'text-stone-400')}>
            ${outstandingTotal.toFixed(0)}
          </p>
          <p className="text-xs mt-1 text-stone-400">across {all.filter(i => !['paid', 'cancelled'].includes(i.status)).length} invoices</p>
        </div>
        <div className="card p-4">
          <p className="section-label">Overdue</p>
          <p className={cn('text-2xl font-bold mt-1', overdueTotal > 0 ? 'text-danger' : 'text-stone-400')}>
            ${overdueTotal.toFixed(0)}
          </p>
          <p className="text-xs mt-1 text-stone-400">{overdueInvs.length} invoice{overdueInvs.length !== 1 ? 's' : ''} past due</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-stone-700">Monthly Revenue</h3>
            <div className="flex items-center gap-3 text-2xs text-stone-400">
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-brand-200 inline-block" /> Invoiced</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-brand-600 inline-block" /> Collected</span>
            </div>
          </div>
          <div className="flex items-end gap-1.5" style={{ height: '96px' }}>
            {(() => {
              const maxVal = Math.max(...monthsData.map(d => d.invoiced), 1)
              return monthsData.map(d => {
                const invH = Math.round((d.invoiced / maxVal) * 80)
                const colH = Math.round((d.collected / maxVal) * 80)
                return (
                  <div key={d.key} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full relative" style={{ height: '80px' }}>
                      {invH > 0 && (
                        <div
                          className={cn('absolute bottom-0 left-0 right-0 rounded-t-sm', d.isCurrent ? 'bg-brand-200' : 'bg-stone-200')}
                          style={{ height: `${invH}px` }}
                          title={`Invoiced: $${d.invoiced.toFixed(0)}`}
                        />
                      )}
                      {colH > 0 && (
                        <div
                          className={cn('absolute bottom-0 left-0 right-0 rounded-t-sm', d.isCurrent ? 'bg-brand-600' : 'bg-stone-400')}
                          style={{ height: `${colH}px` }}
                          title={`Collected: $${d.collected.toFixed(0)}`}
                        />
                      )}
                      {d.isCurrent && d.invoiced > 0 && (
                        <div className="absolute -top-5 left-0 right-0 text-center text-2xs font-semibold text-brand-700">
                          ${d.invoiced.toFixed(0)}
                        </div>
                      )}
                    </div>
                    <span className={cn('text-2xs', d.isCurrent ? 'text-brand-700 font-semibold' : 'text-stone-400')}>
                      {d.label}
                    </span>
                  </div>
                )
              })
            })()}
          </div>
          <p className="text-2xs text-stone-400 mt-3 text-center">
            Target more each month — every buyer re-order counts
          </p>
        </div>

        <div className="card p-4">
          <h3 className="text-sm font-semibold text-stone-700 mb-3">
            Follow-Up Needed
            {overdueInvs.length > 0 && (
              <span className="ml-2 text-2xs font-semibold text-white bg-danger px-1.5 py-0.5 rounded-full">
                {overdueInvs.length}
              </span>
            )}
          </h3>
          {overdueInvs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <span className="text-2xl mb-2">✓</span>
              <p className="text-sm text-stone-500">No overdue invoices</p>
              <p className="text-2xs text-stone-400 mt-1">All caught up!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {overdueInvs.slice(0, 5).map(inv => {
                const buyer = inv.buyer ?? allBuyers.find(b => b.id === inv.buyer_id)
                const total = computeTotal(inv)
                const due = Math.max(0, total - (inv.amount_paid ?? 0))
                const daysOverdue = inv.due_date
                  ? Math.floor((now.getTime() - new Date(inv.due_date + 'T23:59:59').getTime()) / 86400000)
                  : 0
                const msg = `Hi ${buyer?.name ?? 'there'}, this is Amruth Ahar Foods. We wanted to follow up on Invoice #${inv.invoice_number} for $${due.toFixed(2)}, which was due on ${inv.due_date}. Please let us know when you expect to process the payment. Thank you!`
                return (
                  <div key={inv.id} className="flex items-start justify-between gap-2 p-2.5 rounded-lg bg-red-50 border border-red-100">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-stone-700">{inv.invoice_number}</span>
                        <span className="text-2xs text-danger font-medium">{daysOverdue}d overdue</span>
                      </div>
                      <p className="text-xs text-stone-600 truncate mt-0.5">{buyer?.name ?? '—'}</p>
                      <p className="text-sm font-bold text-stone-900 mt-0.5">${due.toFixed(2)} due</p>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={() => setModalInvoice(inv)}
                        className="text-2xs px-2 py-1 bg-white border border-stone-200 rounded text-stone-600 hover:bg-stone-50 transition-colors"
                      >
                        View
                      </button>
                      <button
                        onClick={() => { navigator.clipboard.writeText(msg) }}
                        className="text-2xs px-2 py-1 bg-white border border-stone-200 rounded text-stone-600 hover:bg-stone-50 transition-colors"
                        title="Copy follow-up message"
                      >
                        Copy msg
                      </button>
                    </div>
                  </div>
                )
              })}
              {overdueInvs.length > 5 && (
                <p className="text-2xs text-stone-400 text-center pt-1">
                  +{overdueInvs.length - 5} more overdue invoices
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View toggle */}
      {all.length > 0 && (
        <div className="flex items-center gap-1 bg-stone-100 p-0.5 rounded-lg self-start w-fit">
          {(['list', 'by-buyer'] as const).map(v => (
            <button
              key={v}
              onClick={() => setInvoiceView(v)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                invoiceView === v ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              )}
            >
              {v === 'list' ? 'All Invoices' : 'By Buyer'}
            </button>
          ))}
        </div>
      )}

      {/* By-buyer summary */}
      {invoiceView === 'by-buyer' && buyerStats.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Buyer</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Invoices</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Total Invoiced</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Collected</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Outstanding</th>
                <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 w-8" />
              </tr>
            </thead>
            <tbody>
              {buyerStats.map(bs => {
                const isExpanded = expandedBuyer === bs.buyer.id
                return (
                  <>
                    <tr
                      key={bs.buyer.id}
                      className="border-b border-stone-100 hover:bg-stone-50 transition-colors cursor-pointer"
                      onClick={() => setExpandedBuyer(isExpanded ? null : bs.buyer.id)}
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold text-stone-900">{bs.buyer.name}</p>
                        {bs.buyer.buyer_code && (
                          <p className="text-xs text-stone-400 font-mono">{bs.buyer.buyer_code}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-stone-600">{bs.active.length}</td>
                      <td className="px-4 py-3 text-right font-semibold text-stone-900">${bs.totalInv.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-green-700 font-medium">${bs.totalPaid.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={cn(
                          'font-semibold',
                          bs.outstanding > 0 ? (bs.hasOverdue ? 'text-red-600' : 'text-amber-600') : 'text-stone-400'
                        )}>
                          {bs.outstanding > 0 ? `$${bs.outstanding.toFixed(2)}` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {bs.hasOverdue ? (
                          <span className="text-2xs font-semibold bg-red-50 text-red-600 px-2 py-0.5 rounded-full">Overdue</span>
                        ) : bs.outstanding > 0 ? (
                          <span className="text-2xs font-semibold bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">Outstanding</span>
                        ) : (
                          <span className="text-2xs font-semibold bg-green-50 text-green-700 px-2 py-0.5 rounded-full">Clear</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-stone-400">
                        <ChevronRight size={14} className={cn('transition-transform', isExpanded && 'rotate-90')} />
                      </td>
                    </tr>

                    {/* Expanded invoice rows */}
                    {isExpanded && bs.invs.map(inv => {
                      const total = computeTotal(inv)
                      const isOverdue = inv.status !== 'paid' && inv.status !== 'cancelled'
                        && !!inv.due_date && new Date(inv.due_date + 'T23:59:59') < now
                      return (
                        <tr
                          key={inv.id}
                          className="border-b border-stone-50 bg-stone-50/60 hover:bg-stone-100/60 cursor-pointer transition-colors"
                          onClick={e => { e.stopPropagation(); setModalInvoice(inv) }}
                        >
                          <td className="pl-10 pr-4 py-2.5">
                            <span className="font-mono text-xs font-bold text-stone-600">{inv.invoice_number}</span>
                            {inv.po_number && <span className="text-2xs text-stone-400 ml-2">PO {inv.po_number}</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right text-xs text-stone-400">{inv.issued_date}</td>
                          <td className="px-4 py-2.5 text-right text-xs font-medium text-stone-700">${total.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-right text-xs text-green-700">${(inv.amount_paid ?? 0).toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-right text-xs">
                            {inv.status !== 'paid' && inv.status !== 'cancelled' && total - (inv.amount_paid ?? 0) > 0 ? (
                              <span className={isOverdue ? 'text-red-600 font-semibold' : 'text-amber-600'}>
                                ${(total - (inv.amount_paid ?? 0)).toFixed(2)}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-2.5" colSpan={2}>
                            <StatusBadge status={inv.status} />
                          </td>
                        </tr>
                      )
                    })}
                  </>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {invoiceView === 'list' && (all.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <FileText size={32} className="text-stone-300 mb-3" />
          <p className="text-stone-600 font-medium">No invoices yet</p>
          <p className="text-sm text-stone-400 mt-1 mb-4">
            Create your first invoice to send to a buyer.
          </p>
          {canEdit && (
            <Button size="sm" onClick={() => setModalInvoice('new')}>
              + New Invoice
            </Button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Invoice #
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Buyer
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Issued
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Due
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Subtotal
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Tax
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Total
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Status
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {all.map(inv => {
                const buyer = inv.buyer ?? getBuyer(inv.buyer_id)
                const sub = computeSubtotal(inv)
                const total = computeTotal(inv)
                const taxAmt = sub * ((inv.tax_percent ?? 0) / 100)

                return (
                  <tr
                    key={inv.id}
                    className="border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors cursor-pointer"
                    onClick={() => setModalInvoice(inv)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-stone-800 text-xs font-bold">
                        {inv.invoice_number ?? <span className="text-stone-400 font-sans font-normal">Draft</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-stone-900">
                        {buyer?.name ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600">{inv.issued_date}</td>
                    <td className="px-4 py-3 text-stone-600">
                      {inv.due_date
                        ? <span className="flex items-center gap-1.5">
                          <span>{inv.due_date}</span>
                          {inv.payment_terms === 'Net EOM' && (
                            <span className="text-2xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">EOM</span>
                          )}
                        </span>
                        : <span className="text-stone-300">—</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-right text-stone-700">
                      ${sub.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-stone-500 text-xs">
                      {(inv.tax_percent ?? 0) > 0 ? `$${taxAmt.toFixed(2)}` : <span className="text-stone-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-stone-900">
                      ${total.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); setModalInvoice(inv) }}
                          className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                        >
                          {canEdit ? 'Edit' : 'View'}
                        </button>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation()
                            printInvoice(inv, restaurant, allSupplyItems)
                          }}
                          className="text-stone-400 hover:text-stone-600 transition-colors"
                          title="Print"
                        >
                          <Printer size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ))}

      {modalInvoice !== null && (
        <InvoiceModal
          invoice={modalInvoice === 'new' ? null : modalInvoice}
          buyers={allBuyers}
          supplyItems={allSupplyItems}
          onClose={() => setModalInvoice(null)}
          readOnly={!canEdit}
        />
      )}
    </div>
  )
}
